import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../lib/api';

/**
 * Custom WhatsApp/SMS message templates per practice. Extracted verbatim
 * from the legacy SettingsPage.jsx. Patient-facing copy stays German.
 */
export default function TemplatesSection({ className = '' }) {
  const [templates, setTemplates] = useState([]);
  const [maxChars, setMaxChars] = useState(1600);
  const [draft, setDraft] = useState({});
  const [previewing, setPreviewing] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const textareaRefs = React.useRef({});

  const load = React.useCallback(async () => {
    const r = await api.get('/api/templates');
    setTemplates(r.data.templates || []);
    setMaxChars(r.data.max_chars || 1600);
    const d = {};
    for (const t of r.data.templates) d[t.key] = t.current_text;
    setDraft(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  function setText(key, text) {
    setDraft((prev) => ({ ...prev, [key]: text.slice(0, maxChars) }));
  }
  function insertVar(key, variable) {
    const el = textareaRefs.current[key];
    if (!el) { setText(key, (draft[key] || '') + `{${variable}}`); return; }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const before = (draft[key] || '').slice(0, start);
    const after = (draft[key] || '').slice(end);
    const next = `${before}{${variable}}${after}`;
    setText(key, next);
    setTimeout(() => { el.focus(); const pos = start + variable.length + 2; el.setSelectionRange(pos, pos); }, 0);
  }
  async function save(key) {
    setSavingKey(key);
    try { await api.put(`/api/templates/${key}`, { text: draft[key] }); toast.success('Vorlage gespeichert ✓'); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen'); }
    finally { setSavingKey(null); }
  }
  async function reset(key) {
    setSavingKey(key);
    try { await api.delete(`/api/templates/${key}`); toast.success('Auf Standard zurückgesetzt'); await load(); }
    catch (e) { toast.error('Zurücksetzen fehlgeschlagen'); }
    finally { setSavingKey(null); }
  }
  async function preview(key) {
    try { const r = await api.post(`/api/templates/${key}/preview`, { text: draft[key] }); setPreviewing({ key, rendered: r.data.rendered, length: r.data.length }); }
    catch (e) { toast.error('Vorschau fehlgeschlagen'); }
  }

  return (
    <div className={`so-card p-5 ${className}`} data-testid="templates-section">
      <h2 className="text-sm font-semibold mb-1">Nachrichten-Vorlagen</h2>
      <p className="text-xs text-text-secondary mb-4">
        Anpassbare WhatsApp/SMS-Texte für Erinnerungen und Slot-Aktivierungen. <strong>Alle Nachrichten werden auf Deutsch verschickt</strong> — unabhängig von Ihrer UI-Sprache. Variablen wie <code>{'{Vorname}'}</code> oder <code>{'{Datum}'}</code> werden zum Versand-Zeitpunkt eingesetzt.
      </p>
      <div className="space-y-5">
        {templates.map((t) => {
          const text = draft[t.key] ?? t.current_text;
          const length = text.length;
          const dirty = text !== t.current_text;
          const disabledByFlag = !t.enabled;
          return (
            <div key={t.key} className={`rounded-md border p-4 ${disabledByFlag ? 'bg-background/40 border-dashed' : 'bg-white border-border'}`} data-testid={`tpl-row-${t.key}`}>
              <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                <div>
                  <p className="text-sm font-semibold flex items-center gap-2">
                    {t.label}
                    {t.is_overridden && <span className="so-pill so-pill-accent text-[10px]" data-testid={`tpl-overridden-${t.key}`}>Angepasst</span>}
                    {disabledByFlag && <span className="so-pill so-pill-neutral text-[10px]" data-testid={`tpl-disabled-${t.key}`}>Deaktiviert</span>}
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5">{t.description}</p>
                </div>
                <span className={`text-[11px] font-mono ${length > maxChars * 0.9 ? 'text-warning' : 'text-text-secondary'}`} data-testid={`tpl-counter-${t.key}`}>
                  {length} / {maxChars}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`tpl-vars-${t.key}`}>
                {t.variables.map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(t.key, v)} className="text-[11px] px-2 py-0.5 rounded-full bg-accent/10 text-accent hover:bg-accent/20 transition-colors font-mono" data-testid={`tpl-var-${t.key}-${v}`}>{`{${v}}`}</button>
                ))}
              </div>
              <textarea ref={(el) => { textareaRefs.current[t.key] = el; }} rows={4} className="so-input font-sans text-sm leading-relaxed" value={text} onChange={(e) => setText(t.key, e.target.value)} disabled={disabledByFlag} data-testid={`tpl-text-${t.key}`}/>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => save(t.key)} disabled={!dirty || savingKey === t.key || disabledByFlag} className="so-btn so-btn-primary text-xs" data-testid={`tpl-save-${t.key}`}>Speichern</button>
                <button onClick={() => preview(t.key)} disabled={disabledByFlag} className="so-btn so-btn-secondary text-xs" data-testid={`tpl-preview-${t.key}`}>Vorschau</button>
                {t.is_overridden && (
                  <button onClick={() => reset(t.key)} disabled={savingKey === t.key} className="so-btn so-btn-secondary text-xs" data-testid={`tpl-reset-${t.key}`}>Zurücksetzen</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {previewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewing(null)} data-testid="tpl-preview-modal">
          <div className="bg-white rounded-md shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-2">Vorschau</h3>
            <p className="text-xs text-text-secondary mb-3">So sieht die Nachricht beim Patienten aus (Beispiel-Daten):</p>
            <div className="rounded-md bg-success/5 border border-success/30 p-3 text-sm font-sans whitespace-pre-wrap leading-relaxed" data-testid="tpl-preview-text">{previewing.rendered}</div>
            <p className="text-[11px] text-text-secondary mt-2">{previewing.length} Zeichen</p>
            <div className="mt-3 flex justify-end">
              <button onClick={() => setPreviewing(null)} className="so-btn so-btn-secondary text-xs" data-testid="tpl-preview-close">Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
